<script setup lang="ts">
import BackstageButton from '@/components/common/BackstageButton.vue';
import { useAnalytics } from '@/composables/analytics';
import { ButtonVariant } from '@/models/button-variant.enum';
import { GaCustomEvents } from '@/models/ga-custom-events.enum';
import { Icons } from '@/models/icons.enum';
import { OrderType } from '@onrunning/backstage-models';
import { storeToRefs } from 'pinia';
import { computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { AddEditOrderBreadcrumbSteps } from '../../../models/add-edit-order-breadcrumb-steps.enum';
import { useDraftsInitInfoStore } from '../../../stores/drafts-init-info';
import { useUserStore } from '../../../stores/user';
import { toISOString } from '../../../utils/temporalUtils';

const draftsInitInfoStore = useDraftsInitInfoStore();
const { getInitialInfoData } = draftsInitInfoStore;
const { initInfo } = storeToRefs(draftsInitInfoStore);
const { selectedAccountId } = useUserStore();
const { selectedAccount } = storeToRefs(useUserStore());
const { t } = useI18n();
const { addCustomGaEvent } = useAnalytics();
const bgImageUrl = computed(() => `url(${t('viewSelfSellIn.back_link.bg_image_url')})`);

onMounted(() => {
  getInitialInfoData(selectedAccountId);
});

const goToOrderUrl = computed(() => {
  const orderTypeDto = initInfo.value?.orderTypes?.find((ot) => ot.type === OrderType.MultiplePreOrder);
  if (!orderTypeDto) return '/';
  const startDate = toISOString(orderTypeDto.startDate);

  return `/orders/create/${orderTypeDto.type.toLocaleLowerCase()}/${startDate}/${AddEditOrderBreadcrumbSteps.productsModelsSelection}`;
});

const onStartToOrderClick = () => {
  addCustomGaEvent({
    event: GaCustomEvents.SelfSellInOrderCreationClick,
    component: 'BackstageBackLink',
    account_id: selectedAccount.value.id,
    account_name: selectedAccount.value.name,
    account_country: selectedAccount.value.billingCountry,
  });
};
</script>
<template>
  <section class="backstage-back-link" aria-label="backstage-back-link">
    <div class="backstage-back-link__content">
      <h2 data-cy="backstage-back-link__title" class="backstage-back-link__title">
        {{ t('viewSelfSellIn.back_link.title') }}
      </h2>
      <p data-cy="backstage-back-link__subtitle" class="backstage-back-link__subtitle">
        {{ t('viewSelfSellIn.back_link.subtitle') }}
      </p>
      <div class="backstage-back-link__buttons">
        <BackstageButton
          class="backstage-back-link__button"
          :variant="ButtonVariant.PRIMARY_INVERTED"
          :icon-right="Icons.ARROW_RIGHT"
          href="/"
          data-cy-button="backstage-homepage-cta">
          {{ t('viewSelfSellIn.back_link.back_text') }}
        </BackstageButton>

        <BackstageButton
          class="backstage-back-link__button"
          :variant="ButtonVariant.PRIMARY"
          :icon-right="Icons.ARROW_RIGHT"
          :href="goToOrderUrl"
          data-cy-button="backstage-product-selection-cta"
          @click="onStartToOrderClick">
          {{ t('viewSelfSellIn.back_link.start_to_order') }}
        </BackstageButton>
      </div>
    </div>
  </section>
</template>

<style lang="scss" scoped>
.backstage-back-link {
  position: relative;
  width: calc(100% + 48px);
  left: -24px;
  overflow: hidden;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  min-height: 280px;
  padding: 60px 24px;
  background-color: var(--primary-bg-color);
  background-image: v-bind(bgImageUrl);
  background-size: cover;
  background-position: center;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-color: rgba(0, 0, 0, 0.2);
    pointer-events: none;
  }

  &__content {
    z-index: 1;
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 40px;
    width: 600px;
  }

  &__title {
    margin: 0;
    color: var(--secondary-text-color);
    font-size: 3rem;
    font-weight: 700;
    line-height: 1;
    letter-spacing: -0.96px;
  }

  &__subtitle {
    margin: 0;
    color: var(--secondary-text-color);
    font-size: 1rem;
    font-weight: 400;
    line-height: 1.5;
    text-align: left;
  }

  &__buttons {
    display: flex;
    gap: 12px;
  }

  &__button {
    :deep(.primary .icon) {
      background-color: var(--primary-bg-color);
    }
    :deep(.primary-inverted .icon) {
      background-color: var(--secondary-bg-color);
    }
  }
}
</style>
